<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <loading-spinner v-if="isLoading"></loading-spinner>

        <paginated-table
          v-else
          :key="tableKey"
          :title="$t('title.all_submission_status')"
          icon="manage_search"
          :columns="columns"
          :data="paginatedPapers"
          @updatePagination="updatePagination"
        >
          <template v-slot:top-buttons>
            <v-select
              v-model="selectedJournalIdx"
              :items="journalOptions"
              item-text="text"
              item-value="value"
              label="간행물"
              outlined
              dense
              hide-details
              clearable
              class="mr-3"
              style="max-width: 200px"
              @change="onJournalFilterChange"
            ></v-select>
            <v-select
              v-model="selectedCategory"
              :items="categoryOptions"
              item-text="label"
              item-value="value"
              :label="$t('label.paper_category')"
              outlined
              dense
              hide-details
              class="mr-3"
              style="max-width: 160px"
              @change="onCategoryStatusChange"
            ></v-select>
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              :label="$t('label.status')"
              outlined
              dense
              hide-details
              style="max-width: 140px"
              @change="onCategoryStatusChange"
            ></v-select>
          </template>
        </paginated-table>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";

import { getAdminPaperStatusColumns } from "@/fields/paper/columns";

import { paperApi } from "@/api/api";

const CATEGORY_OPTIONS = [
  { value: "SUBMISSION",    label: "투고" },
  { value: "REVIEW",        label: "심사" },
  { value: "FINAL_REVIEW",  label: "총평" },
  { value: "REVISION",      label: "수정" },
  { value: "FINAL_REVISION",label: "수정 후 게재" },
  { value: "PUBLICATION",   label: "게재" },
  { value: "REJECTED",      label: "게재 불가" },
  { value: "RESUBMISSION",  label: "재투고" },
];

const STATUS_OPTIONS = [
  { value: "WAITING",     label: "작업 대기" },
  { value: "IN_PROGRESS", label: "작업중" },
  { value: "COMPLETED",   label: "작업완료" },
];

export default {
  components: {
    PaginatedTable,
    LoadingSpinner,
  },
  data() {
    return {
      rawPapers: [],
      isLoading: false,
      selectedCategory: "SUBMISSION",
      selectedStatus: "WAITING",
      selectedJournalIdx: null,
      categoryOptions: CATEGORY_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      currentPage: 1,
      currentSize: 5,
      tableKey: 0,
      columns: getAdminPaperStatusColumns({
        edit: this.handleEdit,
        showDetail: this.handleShowPaperDetail,
      }),
    };
  },
  computed: {
    journalOptions() {
      const seen = new Set();
      return this.rawPapers
        .filter(p => p.journal && p.journal.title)
        .filter(p => {
          if (seen.has(p.journal.title)) return false;
          seen.add(p.journal.title);
          return true;
        })
        .map(p => ({ text: p.journal.title, value: p.journal.title }));
    },
    filteredPapers() {
      if (!this.selectedJournalIdx) return this.rawPapers;
      return this.rawPapers.filter(p => p.journal && p.journal.title === this.selectedJournalIdx);
    },
    paginatedPapers() {
      const start = (this.currentPage - 1) * this.currentSize;
      const end = start + this.currentSize;
      return {
        elements: this.filteredPapers.slice(start, end),
        page_info: {
          page: this.currentPage,
          size: this.currentSize,
          total_elements: this.filteredPapers.length,
          total_pages: Math.ceil(this.filteredPapers.length / this.currentSize) || 1,
        },
      };
    },
  },
  methods: {
    toEditPaper(paper) {
      this.$router.push({
        name: "submission-edit",
        params: { idx: paper.idx },
      });
    },
    toPaperDetail(paper) {
      this.$router.push({
        name: "submission-detail",
        params: { idx: paper.idx },
      });
    },
    onCategoryStatusChange() {
      this.currentPage = 1;
      this.fetchPapers();
    },
    onJournalFilterChange() {
      this.currentPage = 1;
      this.tableKey++;
    },
    updatePagination(pagination) {
      this.currentSize = pagination.size;
      this.currentPage = pagination.page;
    },
    async fetchPapers() {
      const params = {
        category: this.selectedCategory,
        status: this.selectedStatus,
        page: 1,
        size: 1000,
        sort_by: "idx",
        direction: "desc",
      };

      try {
        this.isLoading = true;
        const response = await paperApi.getPapers(params);
        this.rawPapers = response?.data?.response?.elements || [];
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async handleEdit(paper) {
      if (
        !["SUBMISSION", "REVISION", "FINAL_REVISION"].includes(paper.category) ||
        paper.status === "COMPLETED"
      ) {
        this.notifyVue({
          message: this.$t("alert.can_not_edit_paper"),
          horizontal: "center",
          style: "danger",
        });
        return;
      }
      await this.toEditPaper(paper);
    },
    async handleShowPaperDetail(paper) {
      await this.toPaperDetail(paper);
    },
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message,
        icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  mounted() {
    this.fetchPapers();
  },
};
</script>
<style scoped>
.v-select {
  background-color: white;
}
</style>
